import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { stopProfileLoading } from '../store/slices/auth.slice'
import { useSelector, useDispatch } from 'react-redux'
import { lazy, Suspense, useEffect } from 'react'
import { fetchUserProfile } from '../store/api/auth.thunk'
import { addNotification } from '../store/slices/notification.slice'
import { getSocket } from '../lib/socket'
import { Toaster } from 'react-hot-toast'
import { useSocketEvents } from './hooks/useSocketEvents'
import { ThemeProvider } from './context/ThemeContext'
import './App.css'

const Login = lazy(() => import('./auth/Login'))
// Registration is now handled via OAuth on the login page
const ForgotPassword = lazy(() => import('./auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./auth/ResetPassword'))
const Home = lazy(() => import('./pages/Home'))
const Navbar = lazy(() => import('./components/Navbar'))
const MainLayout = lazy(() => import('./components/MainLayout'))
const Problem = lazy(() => import('./components/Problem').then(m => ({ default: m.Problem })))
const ProblemDetail = lazy(() => import('./components/ProblemDetails').then(m => ({ default: m.ProblemDetail })))
const BattleArena = lazy(() => import('./pages/BattleArena'))
const TournamentBattleArena = lazy(() => import('./pages/TournamentBattleArena'))
const Battle = lazy(() => import('./pages/Battle').then(m => ({ default: m.Battle })))
const Leaderboard = lazy(() => import('./pages/Leaderboard').then(m => ({ default: m.Leaderboard })))
const JoinRoom = lazy(() => import('./pages/JoinRoom'))
const FindMatch = lazy(() => import('./pages/FindMatch').then(m => ({ default: m.FindMatch })))
const TeamBattle = lazy(() => import('./pages/TeamBattle').then(m => ({ default: m.TeamBattle })))
const Team = lazy(() => import('./pages/Team'))
const TeamTournament = lazy(() => import('./pages/TeamTournament'))
const BattleRoom = lazy(() => import('./pages/BattleRoom').then(m => ({ default: m.BattleRoom })))
const SquidMode = lazy(() => import('./pages/SquidMode').then(m => ({ default: m.SquidMode })))
const Admin = lazy(() => import('./pages/Admin'))
const AdminContests = lazy(() => import('./pages/AdminContests'))
const AdminLogin = lazy(() => import('./auth/AdminLogin'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const AdminTournaments = lazy(() => import('./pages/AdminTournaments'))
const SpectatorArena = lazy(() => import('./pages/SpectatorArena'))
const LiveArenas = lazy(() => import('./pages/LiveArenas'))
const Profile = lazy(() => import('./pages/Profile'))
const Contests = lazy(() => import('./pages/Contests'))
const ContestDetail = lazy(() => import('./pages/ContestDetail'))
const ContestArena = lazy(() => import('./pages/ContestArena'))
const Tournaments = lazy(() => import('./pages/Tournaments'))
const TournamentBracket = lazy(() => import('./pages/TournamentBracket'))
const Achievements = lazy(() => import('./pages/Achievements'))
const CodChampionship = lazy(() => import('./pages/CodChampionship'))
const Footer = lazy(() => import('./components/Footer'))
const GlobalChat = lazy(() => import('./components/GlobalChat'))
const SocialLobby = lazy(() => import('./components/SocialLobby'))

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const location = useLocation();

  // Public routes that should be accessible without authentication
  const publicPaths = [
    '/login',
    '/admin/login',
    '/forgot-password',
    '/reset-password',
    '/register',
    '/landing-page',
    '/landing_page',
    '/landing',
    '/championship',
  ];
  const isPublic = publicPaths.some((p) => location.pathname.startsWith(p));

  // If authenticated or on a public path, render children; otherwise redirect.
  return isAuthenticated || isPublic ? (
    children
  ) : (
    <Navigate to="/login" replace />
  );
};

// Admin Protected Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, profileLoading } = useSelector((state) => state.auth);
  if (profileLoading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex flex-col items-center justify-center gap-4 text-xs font-mono text-emerald-500">
        Authenticating Secure Link...
      </div>
    );
  }
  return isAuthenticated && user?.role === "ADMIN" ? children : <Navigate to="/admin/login" />;
};

// Global Socket Listener for Redirections
const GlobalSocketListener = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  
  // Initialize socket event handlers for query invalidation
  useSocketEvents();

  useEffect(() => {
    // 1. Handle dynamic import failures (stale build chunks)
    const handleModuleError = (e) => {
      const isModuleError = e.message?.includes('Failed to fetch dynamically imported module') || 
                          e.stack?.includes('Failed to fetch dynamically imported module') ||
                          e.name === 'ChunkLoadError';
      
      if (isModuleError) {
        console.warn('Stale build detected. Reloading for latest assets...');
        window.location.reload();
      }
    };

    window.addEventListener('error', handleModuleError, true);
    window.addEventListener('unhandledrejection', (e) => handleModuleError(e.reason));

    // 2. Original socket listeners
    if (!isAuthenticated || !user) return;

    const socket = getSocket();
    
    // Handle team battle invitations/pulls
    socket.on("team_battle_invite", (data) => {
      console.log("Global Pull Received:", data);
      
      // Store the active battle ID
      localStorage.setItem("active_battle_id", data.battleId);
      
      // Navigate to the battle room
      navigate(`/battle-room/${data.battleId}`, { replace: true });
    });

    socket.on("new_notification", (data) => {
      dispatch(addNotification(data));
    });

    return () => {
      socket.off("team_battle_invite");
      socket.off("new_notification");
    };
  }, [isAuthenticated, user, navigate]);

  return null;
};

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, profileLoading } = useSelector((state) => state.auth);

  // Global session restoration and OAuth token capture on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get("accessToken");
    const urlRefreshToken = params.get("refreshToken");

    if (urlToken) {
      localStorage.setItem("accessToken", urlToken);
      if (urlRefreshToken) localStorage.setItem("refreshToken", urlRefreshToken);
      
      // Clean up URL for security
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Trigger profile fetch immediately since we now have a token
      dispatch(fetchUserProfile()).catch(() => { });
    } else if (!isAuthenticated) {
      const isAuthRoute = ["/login", "/register", "/forgot-password", "/reset-password"].some(path => 
        location.pathname.startsWith(path)
      );
      
      const PUBLIC_ROUTES = ['/', '/leaderboard', '/live', '/contests', '/landing-page', '/landing_page', '/landing', '/championship'];
      const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname) || 
                           location.pathname.startsWith('/landing') || 
                           location.pathname.startsWith('/championship') || 
                           location.pathname.startsWith('/battle/') || 
                           location.pathname.startsWith('/admin/spectate/');

      const hasLocalToken = localStorage.getItem("accessToken");
      
      // Only fetch profile if we have a reason to (has token OR on protected route)
      // If we are on a public route and HAVE NO token, we are a guest - skip fetch.
      if (hasLocalToken || !isPublicRoute) {
        if (!isAuthRoute) {
          dispatch(fetchUserProfile()).catch(() => { });
        }
      } else {
        // Explicitly stop loading for guest users on public routes
        dispatch(stopProfileLoading());
      }
    }
  }, [dispatch, isAuthenticated, location.search, location.pathname]);

  // Global Redirect for Active Battles
  useEffect(() => {
    if (isAuthenticated && !profileLoading) {
      const activeBattleId = localStorage.getItem("active_battle_id");
      const currentPath = location.pathname;
      const idePattern = /^\/battle\/[^/]+\/ide/;

      // Force return to battle ONLY if visiting landing page or trying to start new engagement
      const sensitivePaths = ["/", "/matchmaking", "/join-room", "/battles"];
      if (activeBattleId && sensitivePaths.includes(currentPath) && !idePattern.test(currentPath)) {
        navigate(`/battle/${activeBattleId}/ide`, { replace: true });
      }
    }
  }, [isAuthenticated, profileLoading, location.pathname, navigate]);

  // Non-blocking UI: background profile synchronization should not block app rendering
  const shouldBlockUI = false;

  return (
    <ThemeProvider>
      <MainLayout>
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: 'var(--color-bg-card)',
              color: 'var(--color-text-main)',
              border: '1px solid var(--glass-border)',
              fontSize: '12px',
              fontFamily: 'var(--font-mono)',
              borderRadius: '2px',
            },
            success: {
              iconTheme: {
                primary: 'var(--color-primary)',
                secondary: 'var(--color-bg-dark)',
              },
            },
          }}
        />
        <GlobalSocketListener />
        <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route
          path="/"
          element={<Home />}
        />
        <Route path="/landing-page" element={<CodChampionship />} />
        <Route path="/landing_page" element={<CodChampionship />} />
        <Route path="/landing" element={<CodChampionship />} />
        <Route path="/championship" element={<CodChampionship />} />
          <Route path="/problems" element={
            <ProtectedRoute>
              <Problem />
            </ProtectedRoute>
          } />
          <Route path='/problem/:id' element={
            <ProtectedRoute>
              <ProblemDetail />
            </ProtectedRoute>
          } />
          <Route path='/battles' element={
            <ProtectedRoute>
              <Battle />
            </ProtectedRoute>
          } />
          <Route path='/battle/:battleId/ide' element={
              <BattleArena />
          } />
          <Route path='/leaderboard' element={<Leaderboard />} />

          <Route path="/join-room" element={
            <ProtectedRoute>
              <JoinRoom />
            </ProtectedRoute>
          } />
          <Route path='/matchmaking' element={
            <ProtectedRoute>
              <FindMatch />
            </ProtectedRoute>
          } />
          <Route path='/team-battle' element={
            <ProtectedRoute>
              <TeamBattle />
            </ProtectedRoute>
          } />
          <Route path='/team' element={
            <ProtectedRoute>
              <Team />
            </ProtectedRoute>
          } />
          <Route path='/team-tournament' element={
            <ProtectedRoute>
              <TeamTournament />
            </ProtectedRoute>
          } />
          <Route path='/team-tournament/:tournamentId' element={
            <ProtectedRoute>
              <TeamTournament />
            </ProtectedRoute>
          } />
          <Route path='/team-tournaments' element={
            <ProtectedRoute>
              <TeamTournament />
            </ProtectedRoute>
          } />
          <Route path='/battle-room/:battleId' element={
            <ProtectedRoute>
              <BattleRoom />
            </ProtectedRoute>
          } />
          <Route path='/tournament/arena/:battleId' element={
            <ProtectedRoute>
              <TournamentBattleArena />
            </ProtectedRoute>
          } />
          <Route path='/squid-game' element={
            <ProtectedRoute>
              <SquidMode />
            </ProtectedRoute>
          } />
          <Route path='/profile/:username' element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path='/squid-game/:gameId' element={
            <ProtectedRoute>
              <SquidMode />
            </ProtectedRoute>
          } />
          <Route path='/squid-game/:gameId/host' element={
            <ProtectedRoute>
              <SquidMode />
            </ProtectedRoute>
          } />
          <Route path='/admin/login' element={<AdminLogin />} />
          <Route path='/admin/dashboard' element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
          <Route path='/admin' element={<Navigate to="/admin/dashboard" replace />} />
          <Route path='/admin/questions' element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          } />
          <Route path='/admin/contests' element={
            <AdminRoute>
              <AdminContests />
            </AdminRoute>
          } />
          <Route path="/admin/spectate/:battleId" element={
            <AdminRoute>
              <SpectatorArena />
            </AdminRoute>
          } />
          <Route path="/live" element={<LiveArenas />} />
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/tournaments/:id" element={
            <ProtectedRoute>
              <TournamentBracket />
            </ProtectedRoute>
          } />
          <Route path="/admin/tournaments" element={
            <AdminRoute>
              <AdminTournaments />
            </AdminRoute>
          } />
          <Route path="/admin/tournaments/:id" element={
            <AdminRoute>
              <TournamentBracket />
            </AdminRoute>
          } />
          <Route path="/contests" element={<Contests />} />
          <Route path="/contests/:id" element={
            <ProtectedRoute>
              <ContestDetail />
            </ProtectedRoute>
          } />
          <Route path="/contest/:contestId/arena/:problemId" element={
            <ProtectedRoute>
              <ContestArena />
            </ProtectedRoute>
          } />
          <Route path="/achievements" element={
            <ProtectedRoute>
              <Achievements />
            </ProtectedRoute>
          } />
        </Routes>
      </MainLayout>
    </ThemeProvider>
  )
}

export default App
